import { type HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

// Re-authored from ../design-system/components/badges/ChannelBadge.jsx.
// Standalone (not composing Badge) because Booking.com/Airbnb use official
// external brand hex — not DS tokens — and their dot color ≠ fg color, so
// Badge's bg-current dot would be wrong.

export type BookingChannel = 'direct' | 'booking' | 'airbnb';

export interface ChannelBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  channel: BookingChannel;
  square?: boolean;
  bare?: boolean;
}

interface ChannelConfig {
  label: string;
  /** dot background — may differ from fg (e.g. Airbnb dot #ff385c vs fg #e11d48) */
  color: string;
  bg: string;
  fg: string;
}

const CHANNELS: Record<BookingChannel, ChannelConfig> = {
  // DS token colors — direct uses DS gold palette
  direct: {
    label: 'Direct',
    color: 'var(--ollio-gold)',
    bg: 'var(--ollio-gold-light)',
    fg: 'var(--ollio-gold-deep)',
  },
  // Official channel brand colors — not DS tokens
  booking: {
    label: 'Booking.com',
    color: '#003580',
    bg: '#eaf0f7',
    fg: '#003580',
  },
  // Official channel brand colors — not DS tokens
  airbnb: {
    label: 'Airbnb',
    color: '#ff385c',
    bg: '#ffe9ee',
    fg: '#e11d48',
  },
};

export function ChannelBadge({
  channel,
  square = false,
  bare = false,
  className,
  ...rest
}: ChannelBadgeProps) {
  const c = CHANNELS[channel];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-[7px] font-sans font-semibold text-xs leading-none whitespace-nowrap',
        bare ? 'p-0' : square ? 'px-[11px] py-[5px] rounded-sm' : 'px-[11px] py-[5px] rounded-full',
        className,
      )}
      style={bare ? { color: c.fg } : { background: c.bg, color: c.fg }}
      data-bare={bare ? 'true' : undefined}
      {...rest}
    >
      <span
        data-dot
        className="h-2 w-2 flex-none rounded-full"
        style={{ background: c.color }}
      />
      {c.label}
    </span>
  );
}
